import { observer } from 'mobx-react-lite';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import {
    tableMenuInsert,
    tableMenuInsertAbove,
    tableMenuInsertBelow,
    tableMenuInsertLeft,
    tableMenuInsertRight,
    tableMenuDelete,
    tableMenuDeleteTable,
    tableMenuDeleteColumn,
    tableMenuDeleteRow,
    tableMenuMerge,
    tableMenuMergeAbove,
    tableMenuMergeBelow,
    tableMenuMergeLeft,
    tableMenuMergeRight,
    tableMenuSplit,
    tableMenuSplitHorizontally,
    tableMenuSplitVertically,
    tableMenuStyle,
    tableMenuStyleDefault,
    tableMenuStyleGrid,
    tableMenuStyleLightLines,
    tableMenuStyleTwoTones,
    tableMenuStyleLightBands,
    tableMenuStyleClear,
    tableMenuHeaderRow,
} from './TableEditContextMenu.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import TableEditPlugin from '../plugin/TableEditPlugin';
import TableEditViewState from '../store/schema/TableEditViewState';
import setTableEditMenuViewState from '../actions/setTableEditMenuViewState';
import { PluginUIProps } from 'owa-editor/lib/store/schema/PluginWithUI';

import { ContextualMenu } from '@fluentui/react/lib/ContextualMenu';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { TableOperation } from 'roosterjs-editor-types';

export default observer(function TableEditContextMenu(
    props: PluginUIProps<TableEditViewState, TableEditPlugin>
) {
    const menuItems = useComputed((): IContextualMenuItem[] => {
        return [
            {
                key: 'insert',
                name: loc(tableMenuInsert),
                subMenuProps: {
                    items: [
                        {
                            key: 'insertAbove',
                            name: loc(tableMenuInsertAbove),
                            data: TableOperation.InsertAbove,
                            onClick: editTable,
                        },
                        {
                            key: 'insertBelow',
                            name: loc(tableMenuInsertBelow),
                            data: TableOperation.InsertBelow,
                            onClick: editTable,
                        },
                        {
                            key: 'insertLeft',
                            name: loc(tableMenuInsertLeft),
                            data: TableOperation.InsertLeft,
                            onClick: editTable,
                        },
                        {
                            key: 'insertRight',
                            name: loc(tableMenuInsertRight),
                            data: TableOperation.InsertRight,
                            onClick: editTable,
                        },
                    ],
                },
            },
            {
                key: 'delete',
                name: loc(tableMenuDelete),
                subMenuProps: {
                    items: [
                        {
                            key: 'deleteTable',
                            name: loc(tableMenuDeleteTable),
                            data: TableOperation.DeleteTable,
                            onClick: editTable,
                        },
                        {
                            key: 'deleteColumn',
                            name: loc(tableMenuDeleteColumn),
                            data: TableOperation.DeleteColumn,
                            onClick: editTable,
                        },
                        {
                            key: 'deleteRow',
                            name: loc(tableMenuDeleteRow),
                            data: TableOperation.DeleteRow,
                            onClick: editTable,
                        },
                    ],
                },
            },
            {
                key: 'merge',
                name: loc(tableMenuMerge),
                subMenuProps: {
                    items: [
                        {
                            key: 'mergeAbove',
                            name: loc(tableMenuMergeAbove),
                            data: TableOperation.MergeAbove,
                            onClick: editTable,
                        },
                        {
                            key: 'mergeBelow',
                            name: loc(tableMenuMergeBelow),
                            data: TableOperation.MergeBelow,
                            onClick: editTable,
                        },
                        {
                            key: 'mergeLeft',
                            name: loc(tableMenuMergeLeft),
                            data: TableOperation.MergeLeft,
                            onClick: editTable,
                        },
                        {
                            key: 'mergeRight',
                            name: loc(tableMenuMergeRight),
                            data: TableOperation.MergeRight,
                            onClick: editTable,
                        },
                    ],
                },
            },
            {
                key: 'split',
                name: loc(tableMenuSplit),
                subMenuProps: {
                    items: [
                        {
                            key: 'splitHorizontally',
                            name: loc(tableMenuSplitHorizontally),
                            data: TableOperation.SplitHorizontally,
                            onClick: editTable,
                        },
                        {
                            key: 'splitVertically',
                            name: loc(tableMenuSplitVertically),
                            data: TableOperation.SplitVertically,
                            onClick: editTable,
                        },
                    ],
                },
            },
            {
                key: 'style',
                name: loc(tableMenuStyle),
                subMenuProps: {
                    items: [
                        {
                            key: 'styleDefault',
                            name: loc(tableMenuStyleDefault),
                            data: 'Default',
                            onClick: formatTable,
                        },
                        {
                            key: 'styleGrid',
                            name: loc(tableMenuStyleGrid),
                            data: 'Grid',
                            onClick: formatTable,
                        },
                        {
                            key: 'styleLightLines',
                            name: loc(tableMenuStyleLightLines),
                            data: 'LightLines',
                            onClick: formatTable,
                        },
                        {
                            key: 'styleTwoTones',
                            name: loc(tableMenuStyleTwoTones),
                            data: 'TwoTones',
                            onClick: formatTable,
                        },
                        {
                            key: 'styleLightBands',
                            name: loc(tableMenuStyleLightBands),
                            data: 'LightBands',
                            onClick: formatTable,
                        },
                        {
                            key: 'styleClear',
                            name: loc(tableMenuStyleClear),
                            data: 'Clear',
                            onClick: formatTable,
                        },
                    ],
                },
            },
            {
                key: 'headers',
                name: loc(tableMenuHeaderRow),
                isChecked: props.viewState.hasHeaders,
                canCheck: true,
                onClick: setTableHeaders,
            },
        ];
    });
    const onDismiss = () => {
        setTableEditMenuViewState(props.viewState, null, props.viewState.hasHeaders);
    };
    const setTableHeaders = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        onDismiss();
        props.plugin.setTableHeaders(props.viewState.hasHeaders);
    };
    const editTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        onDismiss();
        let operation = item.data as TableOperation;
        props.plugin.editTable(operation);
    };
    const formatTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        onDismiss();
        let formatName = item.data as string;
        props.plugin.formatTable(formatName);
    };
    let { position } = props.viewState;
    return (
        position && (
            <ContextualMenu
                shouldFocusOnMount={true}
                target={position}
                onDismiss={onDismiss}
                items={menuItems.get()}
            />
        )
    );
});
